import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Chip,
  Button,
  Container,
  Grid,
  Stack,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Controller, useForm, FormProvider } from 'react-hook-form';
import * as Yup from 'yup';
import { useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import { RHFTextField } from 'src/components/hook-form';
import RHFMarkdownEditor from 'src/components/markdown-editor/markdownEditor';
import RHFImageUpload from 'src/components/hook-form/rhf-image-upload';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import { getPackageDetails, updatePackage } from 'src/services/package.service';
import { getClinicDoctors } from 'src/services/clinic.service';
import { useParams } from 'src/routes/hooks';
import { searchProcedures } from 'src/services/procedures.service';

export default function EditClinicPackageView() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const { packageId } = useParams();

  const [loading, setLoading] = useState(true);
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [procedureOptions, setProcedureOptions] = useState([]);
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [selectedConcerns, setSelectedConcerns] = useState([]);
  const [packagePromoImage, setPackagePromoImage] = useState([]);
  const [prePostImages, setPrePostImages] = useState([]);
  const [packageDescription, setPackageDescription] = useState('');
  const [loadingProcedures, setLoadingProcedures] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const fileName = `package_promo_${new Date().toISOString()}`;
  const folderName = `images/packages/${user.clinic_id}`;
  const prePostImagesFileName = `package_pre_post_images_${new Date().toISOString()}`;
  const prePostImagesFolderName = `images/packages/pre_post_images/${user.clinic_id}`;

  const schema = Yup.object().shape({
    package_name: Yup.string().required(),
    price: Yup.number().required(),
    package_doctors: Yup.array().nullable(),
    tags: Yup.array().nullable(),
    concerns: Yup.array().nullable(),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      package_name: '',
      price: 0,
      package_doctors: [],
      tags: [],
      concerns: [],
    },
  });

  const { control, reset, setValue, getValues } = methods;

  useEffect(() => {
    const load = async () => {
      try {
        const [pkg, doctors] = await Promise.all([
          getPackageDetails(packageId),
          getClinicDoctors(user.clinic_id),
        ]);

        setAvailableDoctors(doctors || []);
        setPackagePromoImage(pkg.package_promo_image || []);
        setPrePostImages(pkg.pre_post_images || []);
        setPackageDescription(pkg.package_description || '');

        const procedures = (pkg.procedure_id || []).map((p) => ({
          id: p.id || p._id,
          name: p.name,
          body_part: p.body_part,
          treatment_id: p.treatment_id || [],
        }));

        setSelectedProcedures(procedures);

        const allConcerns = procedures.flatMap(proc =>
          (proc.treatment_id || []).flatMap(t =>
            (t.concerns || []).map(c => ({ id: c.id || c._id, name: c.name || c }))
          )
        );

        const uniqueConcerns = Array.from(new Map(allConcerns.map(c => [c.id, c])).values());
        setSelectedConcerns(uniqueConcerns);

        const tags = uniqueConcerns.map(c => c.name);

        reset({
          package_name: pkg.package_name,
          price: pkg.price,
          package_doctors: pkg.package_doctors || [],
          tags,
          concerns: uniqueConcerns.map(c => c.id),
          package_promo_image: pkg.package_promo_image || [],
          pre_post_images: pkg.pre_post_images || [],
        });

        setValue('concerns', uniqueConcerns.map(c => c.id));
        setValue('tags', tags);
      } catch {
        setSnackbarMessage('Failed to load package');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [packageId, user.clinic_id, reset, setValue]);

  const handleSave = async () => {
    const data = getValues();
    const payload = {
      clinic_id: user.clinic_id,
      ...data,
      procedure_id: selectedProcedures.map(p => p.id),
      concerns: selectedConcerns.map(c => c.id),
      package_promo_image: packagePromoImage,
      pre_post_images: prePostImages,
    };
    try {
      await updatePackage(packageId, payload);
      setSnackbarMessage('Package updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      navigate(-1);
    } catch (e) {
      setSnackbarMessage(e.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleProcedureInput = async (e, value, reason) => {
    if (reason !== 'input' || value.length < 2) return;
    setLoadingProcedures(true);
    const results = await searchProcedures(value);
    setProcedureOptions(
      (results || []).map((p) => ({
        ...p,
        id: p.id || p._id,
        treatment_id: p.treatment_id || [],
      }))
    );
    setLoadingProcedures(false);
  };

  if (loading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const getProcedureLabel = (o) => [o.body_part, o.name].filter(Boolean).join(' → ');

  return (
    <Container sx={{ maxWidth: 840, mt: 4 }}>
      <Stack direction="row" alignItems="center" mb={2} gap={2}>
        <IconButton onClick={() => navigate(-1)}>
          <Iconify icon="tabler:chevron-left" />
        </IconButton>
        <Typography variant="h4">Edit Package</Typography>
      </Stack>

      <FormProvider {...methods}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <RHFTextField name="package_name" label="Package Name" />
          </Grid>

          <Grid item xs={12} lg={6}>
            <Controller
              name="package_doctors"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={availableDoctors}
                  getOptionLabel={(o) => `${o.user_id.first_name} ${o.user_id.last_name}`}
                  value={field.value.map(id => availableDoctors.find(d => d.user_id.id === id)).filter(Boolean)}
                  onChange={(e, v) => field.onChange(v.map(d => d.user_id.id))}
                  renderInput={(params) => <TextField {...params} label="Doctors" />}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <RHFMarkdownEditor
              name="package_description"
              defaultValue={packageDescription}
              folderName="images/packages"
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <Autocomplete
              multiple
              options={procedureOptions}
              value={selectedProcedures}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              getOptionLabel={getProcedureLabel}
              onChange={(e, v) => {
                setSelectedProcedures(v);

                // Collect all concerns from selected procedures
                const allConcerns = v.flatMap(proc => proc.concerns || []);

                // Remove duplicates
                const uniqueConcerns = Array.from(new Map(allConcerns.map(c => [c.id, c])).values());

                setSelectedConcerns(uniqueConcerns);
                setValue('concerns', uniqueConcerns.map(c => c.id));
                setValue('tags', uniqueConcerns.map(c => c.name));
              }}
              onInputChange={handleProcedureInput}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Procedures"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProcedures && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <Autocomplete
              multiple
              options={selectedConcerns}
              value={selectedConcerns}
              getOptionLabel={(o) => o.name}
              renderTags={(v, getTagProps) => v.map((o, i) => <Chip label={o.name} {...getTagProps({ index: i })} />)}
              renderInput={(params) => <TextField {...params} label="Concerns" />}
              disabled
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="tags"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={field.value}
                  onChange={(e, v) => field.onChange(v)}
                  renderTags={(v, getTagProps) => v.map((o, i) => <Chip label={o} {...getTagProps({ index: i })} />)}
                  renderInput={(params) => <TextField {...params} label="Tags" />}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <RHFTextField name="price" label="Price" type="number" />
          </Grid>

          <Grid item xs={12} lg={6}>
            <RHFImageUpload
              initialImage={packagePromoImage}
              fileName={fileName}
              setValue={setValue}
              name="package_promo_image"
              folderName={folderName}
              displayImages
              multipleFiles
              maxFiles={1}
              label="Package Promo Image"
              width={1530}
              height={750}
              required
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <RHFImageUpload
              initialImage={prePostImages}
              fileName={prePostImagesFileName}
              setValue={setValue}
              name="pre_post_images"
              folderName={prePostImagesFolderName}
              filesRequired={2}
              displayImages
              multipleFiles
              maxFiles={10}
              label="Pre/Post Images"
              width={172}
              height={240}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              sx={{ background: '#D92D7F', ':hover': { background: '#D92D7F' } }}
              onClick={handleSave}
            >
              Edit Package
            </Button>
          </Grid>
        </Grid>
      </FormProvider>

      <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity}>{snackbarMessage}</Alert>
      </Snackbar>
    </Container>
  );
}
